<template>
  <div class="tablero-lavadero">
    <v-row no-gutters class="fill-height">
      <v-col
        v-for="columna in columnasLocales"
        :key="columna.estado"
        :ref="`columna-${columna.estado}`"
        cols="12"
        sm="4"
        class="columna-kanban pa-2"
      >
        <!-- Cabecera de columna -->
        <div
          class="columna-header d-flex align-center justify-space-between mb-2 pa-2 rounded"
          :class="headerClass(columna.estado)"
        >
          <span class="font-weight-bold text-uppercase text-body-2">
            {{ columna.titulo }}
          </span>
          <v-badge
            :content="columna.turnos.length"
            :color="badgeColor(columna.estado)"
            inline
          />
        </div>

        <!-- Zona de drop -->
        <draggable
          :list="columna.turnos"
          :group="{ name: 'lavadero', pull: true, put: true }"
          :disabled="isMobileLayout"
          class="columna-body"
          draggable=".lavadero-draggable-item"
          ghost-class="ghost"
          chosen-class="chosen"
          drag-class="drag"
          fallback-class="lavadero-fallback"
          :animation="150"
          :force-fallback="true"
          :fallback-on-body="true"
          @change="onDragChange($event, columna)"
        >
          <div
            v-for="turno in columna.turnos"
            :key="turnoKey(turno)"
            class="lavadero-draggable-item"
          >
            <TarjetaLavadero
              :turno="turno"
              :puede-reasignar="puedeReasignarTurno(turno)"
              :puede-cambiar-horario="puedeCambiarHorarioTurno(turno)"
              :puede-modificar-unidad="puedeModificarUnidadTurno(turno)"
              @reassign="onReassign"
              @cambiar-horario="onCambiarHorario"
              @modificar-unidad="onModificarUnidad"
              @swipe-action="onSwipeAction"
            />
          </div>
          <!-- Placeholder cuando la columna está vacía -->
          <div
            v-if="columna.turnos.length === 0"
            class="text-center text-caption grey--text pa-4"
          >
            Sin turnos
          </div>
        </draggable>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import TarjetaLavadero from "./TarjetaLavadero.vue";

export default {
  name: "TableroLavadero",
  components: {
    draggable,
    TarjetaLavadero,
  },
  props: {
    columnas: {
      type: Array,
      required: true,
      validator: (val) =>
        val.every(
          (c) =>
            c.titulo && c.estado && Array.isArray(c.turnos)
        ),
    },
    permisos: {
      type: Object,
      default: () => ({ lectura: false, gestion: false, esAdmin: false, esLavador: false, radaCambiarHorario: false }),
    },
    currentUsername: {
      type: String,
      default: "",
    },
    lavadores: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      columnasLocales: [],
      isMobileLayout: false,
      ultimoDragRemovido: null,
    };
  },
  watch: {
    columnas: {
      immediate: true,
      deep: true,
      handler(val) {
        this.columnasLocales = JSON.parse(JSON.stringify(val));
      },
    },
  },
  methods: {
    updateMobileLayout() {
      this.isMobileLayout =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 599px)").matches;
    },
    headerClass(estado) {
      const isDark = this.$vuetify.theme.dark;
      const map = {
        pendiente: isDark ? "amber darken-3" : "amber lighten-4",
        asignado: isDark ? "blue darken-3" : "blue lighten-4",
        finalizado: isDark ? "green darken-3" : "green lighten-4",
      };
      return map[estado] || "";
    },
    badgeColor(estado) {
      const isDark = this.$vuetify.theme.dark;
      const map = {
        pendiente: isDark ? "amber lighten-1" : "warning",
        asignado: isDark ? "blue lighten-1" : "info",
        finalizado: isDark ? "green lighten-1" : "success",
      };
      return map[estado] || "grey";
    },
    turnoKey(turno) {
      return `${turno.fuente || "turno"}-${turno.id}`;
    },
    findTurnoLocal(turno) {
      const key = this.turnoKey(turno);

      for (let columnIndex = 0; columnIndex < this.columnasLocales.length; columnIndex++) {
        const columna = this.columnasLocales[columnIndex];
        const turnoIndex = columna.turnos.findIndex((item) => this.turnoKey(item) === key);

        if (turnoIndex !== -1) {
          return { columna, columnIndex, turnoIndex, turno: columna.turnos[turnoIndex] };
        }
      }

      return null;
    },
    moveTurnoLocal({ turno, toEstado, updates = {}, index = 0 }) {
      if (!turno || !toEstado) return;

      const destino = this.columnasLocales.find((columna) => columna.estado === toEstado);
      if (!destino) return;

      const key = this.turnoKey(turno);
      let turnoLocal = { ...turno };

      this.columnasLocales.forEach((columna) => {
        const turnoIndex = columna.turnos.findIndex((item) => this.turnoKey(item) === key);
        if (turnoIndex !== -1) {
          turnoLocal = { ...columna.turnos[turnoIndex] };
          columna.turnos.splice(turnoIndex, 1);
        }
      });

      const turnoActualizado = {
        ...turnoLocal,
        ...updates,
        estado_lavadero: updates.estado_lavadero || toEstado,
      };
      const safeIndex = Math.max(0, Math.min(index, destino.turnos.length));
      destino.turnos.splice(safeIndex, 0, turnoActualizado);
    },
    updateTurnoLocal(turno, updates = {}) {
      const encontrado = this.findTurnoLocal(turno);
      if (!encontrado) return;

      encontrado.columna.turnos.splice(encontrado.turnoIndex, 1, {
        ...encontrado.turno,
        ...updates,
      });
    },
    revertTurnoLocal(contexto) {
      if (!contexto || !contexto.turno || !contexto.fromEstado) return;

      this.moveTurnoLocal({
        turno: contexto.turno,
        toEstado: contexto.fromEstado,
        updates: {
          ...contexto.turno,
          estado_lavadero: contexto.fromEstado,
        },
        index: contexto.oldIndex || 0,
      });
    },
    /**
     * Maneja el evento change de vuedraggable (cross-list drag & drop).
     * event = { added?, removed?, moved? } — cada uno con { element, newIndex/oldIndex }
     * Se emite drag-change solo cuando un turno cambia de columna (added en destino).
     */
    onDragChange(event, columna) {
      if (event.removed) {
        this.ultimoDragRemovido = {
          key: this.turnoKey(event.removed.element),
          fromEstado: columna.estado,
          oldIndex: event.removed.oldIndex,
        };
        return;
      }

      if (event.added) {
        const turno = event.added.element;
        const key = this.turnoKey(turno);
        const dragRemovido = this.ultimoDragRemovido && this.ultimoDragRemovido.key === key
          ? this.ultimoDragRemovido
          : null;
        const fromEstado = dragRemovido ? dragRemovido.fromEstado : turno.estado_lavadero;
        const toEstado = columna.estado;

        if (fromEstado && fromEstado !== toEstado) {
          this.$emit("drag-change", {
            turno,
            fromEstado,
            toEstado,
            newIndex: event.added.newIndex,
            oldIndex: dragRemovido ? dragRemovido.oldIndex : 0,
          });
        }

        this.ultimoDragRemovido = null;
      }
    },
    /**
     * Determina si el usuario actual puede reasignar este turno.
     * Admin: siempre. Lavador común: solo si está asignado a sí mismo.
     * Se compara por username ya que el frontend no tiene el ID numérico del usuario.
     */
    puedeReasignarTurno(turno) {
      // Turnos finalizados no se pueden reasignar
      if (turno.estado_lavadero === "finalizado") return false;
      if (!turno.lavador_id) return false;
      return !!this.permisos.esAdmin;
    },
    /**
     * Determina si se puede cambiar el horario de retiro de este turno.
     * Solo turnos Rada en pendiente o asignado, con permiso específico.
     */
    puedeCambiarHorarioTurno(turno) {
      if (turno.fuente !== "rada") return false;
      if (turno.estado_lavadero === "finalizado") return false;
      // El permiso lo valida el padre (LavaderoKanban) — aquí solo filtramos por tipo/estado
      return !!this.permisos.radaCambiarHorario;
    },
    /**
     * Determina si se puede modificar la unidad de este turno Rada.
     * Lavadores y administradores pueden hacerlo mientras no esté finalizado.
     */
    puedeModificarUnidadTurno(turno) {
      if (turno.fuente !== "rada") return false;
      if (turno.estado_lavadero === "finalizado") return false;
      return !!(this.permisos.esLavador || this.permisos.esAdmin);
    },
    onReassign(turno) {
      this.$emit("reassign", turno);
    },
    onCambiarHorario(turno) {
      this.$emit("cambiar-horario", turno);
    },
    onModificarUnidad(turno) {
      this.$emit("modificar-unidad", turno);
    },
    onSwipeAction(payload) {
      this.$emit("swipe-action", payload);
    },
    scrollToColumn(estado) {
      const ref = this.$refs[`columna-${estado}`];
      const columna = Array.isArray(ref) ? ref[0] : ref;
      const columnaEl = columna && columna.$el ? columna.$el : columna;
      const tableroEl = this.$el;

      if (!columnaEl || !tableroEl) return;

      if (typeof tableroEl.scrollTo === "function") {
        tableroEl.scrollTo({
          left: columnaEl.offsetLeft,
          behavior: "smooth",
        });
        return;
      }

      tableroEl.scrollLeft = columnaEl.offsetLeft;
    },
  },
  mounted() {
    this.updateMobileLayout();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.updateMobileLayout);
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.updateMobileLayout);
    }
  },
};
</script>

<style scoped>
.tablero-lavadero {
  overflow-x: auto;
  height: 100%;
}

.columna-kanban {
  display: flex;
  flex-direction: column;
}

.columna-body {
  flex: 1;
  min-height: 120px;
  border-radius: 4px;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.02);
}

.lavadero-draggable-item {
  cursor: grab;
}

.lavadero-draggable-item:active {
  cursor: grabbing;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.chosen {
  cursor: grabbing;
}

.drag {
  cursor: grabbing;
}
</style>
